import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from './AddUserModal.module.css';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {
  selectUserRole,
  selectOutlets,
  selectOrganizationId,
  selectOrganizationName,
} from '../../store/selectors/authSelectors';
import { getAvailableRoles, mapRoleToBackend, mapRoleToDisplay } from '../../utils/roleMapper';
import ErrorModal from '../../components/Modals/ErrorModal';


import { CreateUserPayload } from '../../types';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (user: CreateUserPayload) => void;
  error?: string;
  isSubmitting: boolean;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onAddUser, error, isSubmitting }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [outletId, setOutletId] = useState('');
  const [role, setRole] = useState('');
  const [emailError, setEmailError] = useState('');
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });
  const userRole = useSelector(selectUserRole);
  const outlets = useSelector(selectOutlets);
  const organizationId = useSelector(selectOrganizationId);
  const organizationName = useSelector(selectOrganizationName);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Get available roles based on current user's role (RBAC)
  const availableRoles = useMemo(() => {
    const allRoles = getAvailableRoles();
    if (userRole === 'ORG_ADMIN') {
      // ORG_ADMIN can add RECRUITER and MANAGER (not ORGANIZATION ADMIN)
      return allRoles.filter((r) => r !== 'ORGANIZATION ADMIN');
    } else if (userRole === 'OUTLET_MANAGER') {
      // MANAGER can only add RECRUITER
      return allRoles.filter((r) => r === 'RECRUITER');
    }
    return [];
  }, [userRole]);

  // Auto-populate fields when modal opens
  useEffect(() => {
    if (isOpen && userRole) {
      // If OUTLET_MANAGER, auto-populate outlet_id and role (RECRUITER)
      if (userRole === 'OUTLET_MANAGER') {
        if (outlets && outlets.length > 0) {
          setOutletId(outlets[0].outlet_id);
        }
        // Auto-populate role as RECRUITER for MANAGER
        setRole('RECRUITER');
      }
    }
  }, [isOpen, userRole, outlets]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setOutletId('');
      setRole('');
      setEmailError('');
      setErrorModal({ isOpen: false, message: '' });
    }
  }, [isOpen]);

  // Sync prop error to internal error modal
  useEffect(() => {
    if (error) {
      setErrorModal({ isOpen: true, message: error });
    }
  }, [error]);

  // Validate email on change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email before submission
    if (!email || !emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Map display role to backend role before sending
    const backendRole = mapRoleToBackend(role);
    const createUserPayload: CreateUserPayload = {
      full_name: name,
      email,
      outlet_id: outletId,
      organization_id: organizationId || '',
      role: backendRole,
    };
    onAddUser(createUserPayload);
  };

  const handleClose = () => {
    onClose();
  };

  // Don't show form if user doesn't have permission
  if (userRole && userRole !== 'OUTLET_MANAGER' && userRole !== 'ORG_ADMIN') {
    return (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 className={styles.title} data-testid="access-denied-title">Access Denied</h2>
          <p>Only OUTLET_MANAGER and ORG_ADMIN can add users.</p>
          <div className={styles.footer}>
            <button type="button" className={styles.cancelButton} onClick={onClose} data-testid="add-user-cancel">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <form onSubmit={handleSubmit} data-testid="add-user-form">
            <h2 className={styles.title} data-testid="add-user-modal-title">Add New User</h2>

            <div className={styles.formGroup}>
              <label htmlFor="fullName">Full Name*</label>
              <input
                id="fullName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                required
                data-testid="user-name-input"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address*</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailChange}
                className={emailError ? styles.formInputError : ''}
                disabled={isSubmitting}
                required
                data-testid="user-email-input"
              />
              {emailError && <div className={styles.errorMessage}>{emailError}</div>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="role">Role*</label>
              {userRole === 'OUTLET_MANAGER' ? (
                // For MANAGER, show auto-populated read-only field
                <div className={styles.formInputDisabled}>
                  <span>{mapRoleToDisplay('OUTLET_POC')}</span>
                </div>
              ) : (
                // For ORG_ADMIN, show dropdown with available roles
                <select
                  id="role"
                    className={styles.formSelect}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={isSubmitting}
                    required
                  data-testid="user-role-select"
                >
                  <option value="">Select a role</option>
                  {availableRoles.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Show outlet dropdown only for ORG_ADMIN */}
            {userRole === 'ORG_ADMIN' && outlets && outlets.length > 0 && (
              <div className={styles.formGroup}>
                <label htmlFor="outlet">Outlet*</label>
                <select
                  id="outlet"
                  className={styles.formSelect}
                  value={outletId}
                  onChange={(e) => setOutletId(e.target.value)}
                  disabled={isSubmitting}
                  required
                  data-testid="user-outlet-select"
                >
                  <option value="">Select an outlet</option>
                  {outlets.map((outlet) => (
                    <option key={outlet.outlet_id} value={outlet.outlet_id}>
                      {outlet.outlet_name}{' '}
                      {outlet.location ? `(${JSON.stringify(outlet.location)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Show read-only organization info */}
            {organizationName && (
              <div className={styles.formGroup}>
                <label>Organization</label>
                <input
                  type="text"
                  value={organizationName}
                  disabled
                  className={styles.formInputDisabled}
                />
              </div>
            )}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="add-user-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.buttonPrimary}
                disabled={!outletId || !role || isSubmitting}
                data-testid="add-user-submit"
              >
                {isSubmitting ? <LoadingSpinner size="small" /> : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
      />
    </>
  );
};

export default AddUserModal;
